import type { PrizeOutcome } from '~kui/components/KUI/Unified/Completion/types';
import type { Ref } from 'vue';

import { PRIZE_OUTCOME } from '~kui/components/KUI/Unified/Completion/types';

import type { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import type { CollectionCompletionContext } from '~/types/unified';

import { useAnalyticsService } from '~/composables/core';
import { useAccountEligibility } from '~/composables/unified/useAccountEligibility';
import { useUnifiedErrorHandler } from '~/composables/unified/useUnifiedErrorHandler';
import { EVENTS } from '~/constants/events';

import { useCreateUserLearningCampaignEntry } from '../Learn/Campaign/useCreateUserLearningCampaignEntry';

export const usePrizeDrawFlow = (
  collectionCompletion: Ref<CollectionCompletionContext | null> | undefined,
  { origin }: { origin?: AnalyticsOrigin } = {},
) => {
  const { t } = useI18n();
  const { handleError } = useUnifiedErrorHandler();
  const { isAccountComplete } = useAccountEligibility();
  const { createLearningCampaignEntry } = useCreateUserLearningCampaignEntry();
  const analytics = useAnalyticsService();

  const prizeOutcome = ref<PrizeOutcome>(null);
  const confirmPrizeLoading = ref(false);
  const confirmPrizeError = ref<string | null>(null);
  const showProfileVerification = ref(false);
  // Draft answer to the campaign's skill question — lives in flow state so a
  // phone-verification detour doesn't lose what the student typed.
  const skillAnswer = ref('');

  const completionPrize = computed(() => {
    if (!collectionCompletion?.value?.prize) {
      return null;
    }
    return {
      title: collectionCompletion.value.prize.title,
      isUserEligible: collectionCompletion.value.prize.isUserEligible,
      skillQuestion: collectionCompletion.value.prize.skillQuestion ?? null,
    };
  });

  const handleSkipPrizeDraw = () => {
    skillAnswer.value = '';
    prizeOutcome.value = PRIZE_OUTCOME.Cancelled;
  };

  const handleConfirmEntry = async () => {
    const prize = collectionCompletion?.value?.prize;
    const campaignId = prize?.campaignId;
    if (!campaignId) {
      return;
    }

    if (!isAccountComplete.value) {
      showProfileVerification.value = true;
      return;
    }

    confirmPrizeLoading.value = true;
    confirmPrizeError.value = null;

    const answer = prize.skillQuestion ? skillAnswer.value.trim() : null;

    try {
      const { error } = await createLearningCampaignEntry(campaignId, answer);

      if (error) {
        handleError(error, { logMessage: error });
        confirmPrizeError.value = error;
        return;
      }

      analytics.track(EVENTS.UNIFIED_CAMPAIGN_ENTRY_CONFIRMED, {
        campaignId,
        entrySource: 'collection_completion',
        origin: origin ?? null,
        hasSkillAnswer: !!answer,
        answerLength: answer?.length ?? 0,
      });
      prizeOutcome.value = PRIZE_OUTCOME.Confirmed;
    }
    catch (err) {
      handleError(err, { logMessage: 'Failed to create campaign entry' });
      confirmPrizeError.value = t('unified.errors.genericFailed');
    }
    finally {
      confirmPrizeLoading.value = false;
    }
  };

  const handleProfileVerified = async () => {
    await handleConfirmEntry();
  };

  const handleCancelPrizeDraw = () => {
    skillAnswer.value = '';
    prizeOutcome.value = PRIZE_OUTCOME.Cancelled;
  };

  watch(showProfileVerification, (isOpen, wasOpen) => {
    if (wasOpen && !isOpen && prizeOutcome.value === null) {
      prizeOutcome.value = PRIZE_OUTCOME.PhoneSkipped;
    }
  });

  return {
    prizeOutcome,
    confirmPrizeLoading,
    confirmPrizeError,
    showProfileVerification,
    completionPrize,
    skillAnswer,
    handleSkipPrizeDraw,
    handleConfirmEntry,
    handleCancelPrizeDraw,
    handleProfileVerified,
  };
};
